import workCompleted from 'src/assets/placeholders/work-completed.png';

export interface WidgetRegistryEntry {
  key: string;
  label: string;
  description: string;
  feature: string | null;
  singleton: boolean;
  includeInDefault: boolean;
  defaultLayout: any;
}

export function getWidgetRegistry (t: (key: string) => string): WidgetRegistryEntry[] {
  return [
    {
      key: 'weather-forecast',
      label: 'dashboard.weatherForecast',
      description: 'dashboard.weatherForecastDesc',
      feature: null,
      singleton: true,
      includeInDefault: true,
      defaultLayout: {
        type: 'grid', container: false, md: 12, xs: 12, children: [
          { type: 'component', componentName: 'WeatherForecast' }
        ]
      }
    },
    {
      key: 'outlook',
      label: 'dashboard.outlook',
      description: 'dashboard.outlookDesc',
      feature: 'work-tickets',
      singleton: true,
      includeInDefault: true,
      defaultLayout: {
        type: 'grid', container: false, md: 12, xs: 12, children: [
          {
            type: 'block', feature: 'work-tickets', title: t('dashboard.outlook'), children: [
              { type: 'major', title: t('dashboard.criticalAlarmsWithoutNotification'), value: 'alarmStats.criticalAlarmsWithoutNotification', helpText: t('dashboard.criticalAlarmsWithoutNotificationHelp') },
              { type: 'major', title: t('dashboard.newNotificationsLast7Days'), value: 'totalNewNotifications', helpText: t('dashboard.newNotificationsLast7DaysHelp') },
              { type: 'major', title: t('dashboard.circuitsForcedOn'), value: 'alarmStats.forcedOnSummary', defaultValue: null, formatFunction: 'formatForcedOnRatio', helpText: t('dashboard.circuitsForcedOnHelp') },
              { type: 'major', title: t('dashboard.fieldRepairsAssessments'), value: 'alarmStats.outstandingNotificationsByStatus.mnra', helpText: t('dashboard.fieldRepairsAssessmentsHelp'), defaultValue: 0 },
              { type: 'major', title: t('dashboard.totalEstimatedMaintenance'), value: 'alarmStats.estBasicTotalMaintenaceHours', helpText: 'Based on site\'s average time to complete MNRA and WO and predictive failures: %estBasicTotalMaintenanceHoursExplain%', defaultValue: 0 }
            ]
          }
        ]
      }
    },
    {
      key: 'alarm-overview',
      label: 'dashboard.alarmOverview',
      description: 'dashboard.alarmOverviewDesc',
      feature: 'alarm-historian',
      singleton: true,
      includeInDefault: true,
      defaultLayout: {
        type: 'grid', feature: 'alarm-historian', container: false, md: 4, xs: 12, children: [
          {
            type: 'block', title: t('dashboard.alarmOverview'), children: [
              { type: 'minor', title: t('dashboard.totalAlarms'), value: 'alarmStats.totalAlarms', helpText: t('dashboard.totalAlarmsHelp'), defaultValue: 'N/A' },
              { type: 'minor', title: t('dashboard.htcsInAlarm'), value: 'alarmStats.htcsInAlarm', helpText: t('dashboard.htcsInAlarmHelp'), defaultValue: 'N/A' },
              { type: 'minor', title: t('dashboard.htcsInCritAlarm'), value: 'alarmStats.criticalAlarms', helpText: t('dashboard.htcsInCritAlarmHelp'), defaultValue: 'N/A' }
            ]
          }
        ],
        fallback: [
          {
            type: 'block', className: 'no-permission-block', title: t('dashboard.alarmOverview'), children: [
              { type: 'minor', title: t('dashboard.totalAlarms'), value: 'N/A', helpText: t('dashboard.totalAlarmsHelp'), defaultValue: 'N/A' },
              { type: 'minor', title: t('dashboard.htcsInAlarm'), value: 'N/A', helpText: t('dashboard.htcsInAlarmHelp'), defaultValue: 'N/A' },
              { type: 'minor', title: t('dashboard.htcsInCritAlarm'), value: 'N/A', helpText: t('dashboard.htcsInCritAlarmHelp'), defaultValue: 'N/A' }
            ]
          }
        ]
      }
    },
    {
      key: 'system-overview',
      label: 'dashboard.systemOverviewBlock',
      description: 'dashboard.systemOverviewDesc',
      feature: 'alarm-validation',
      singleton: true,
      includeInDefault: true,
      defaultLayout: {
        type: 'grid', feature: 'alarm-validation', container: false, md: 4, xs: 12, children: [
          {
            type: 'block', title: t('dashboard.systemOverviewBlock'), children: [
              { type: 'minor', title: t('dashboard.nuisanceAlarms'), value: 'alarmStats.notesByHtc.nuisance', helpText: t('dashboard.nuisanceAlarmsHelp'), defaultValue: 'N/A' },
              { type: 'minor', title: t('dashboard.loto'), value: 'alarmStats.notesByHtc.loto', helpText: t('dashboard.lotoHelp'), defaultValue: 'N/A' },
              { type: 'minor', title: t('dashboard.offlineDevices'), value: 'alarmStats.deviceStatusSummary.offline', helpText: t('dashboard.offlineDevicesHelp'), defaultValue: 'N/A' }
            ]
          }
        ],
        fallback: [
          {
            type: 'block', className: 'no-permission-block', title: t('dashboard.systemOverviewBlock'), children: [
              { type: 'minor', title: t('dashboard.nuisanceAlarms'), value: 'N/A', helpText: t('dashboard.nuisanceAlarmsHelp'), defaultValue: 'N/A' },
              { type: 'minor', title: t('dashboard.loto'), value: 'N/A', helpText: t('dashboard.lotoHelp'), defaultValue: 'N/A' },
              { type: 'minor', title: t('dashboard.offlineDevices'), value: 'N/A', helpText: t('dashboard.offlineDevicesHelp'), defaultValue: 'N/A' }
            ]
          }
        ]
      }
    },
    {
      key: 'erp-overview',
      label: 'dashboard.erpOverview',
      description: 'dashboard.erpOverviewDesc',
      feature: 'erp-integration',
      singleton: true,
      includeInDefault: true,
      defaultLayout: {
        type: 'grid', feature: 'erp-integration', container: false, md: 4, xs: 12, children: [
          {
            type: 'block', title: t('dashboard.erpOverview'), children: [
              { type: 'minor', title: '%mnraLabel%', value: 'alarmStats.outstandingNotificationsByStatus.mnra', helpText: 'Total open SAP tickets requiring engineering', defaultValue: 'N/A' },
              { type: 'minor', title: '%engRequiredLabel%', value: 'alarmStats.outstandingNotificationsByStatus.eng_required', helpText: 'Total open SAP Work Orders', defaultValue: 'N/A' },
              { type: 'minor', title: '%workOrderLabel%', value: 'alarmStats.totalNotificationsWithWorkOrders', helpText: 'Total open SAP notifications for an approved minor repair', defaultValue: 'N/A' }
            ]
          }
        ],
        fallback: [
          {
            type: 'block', className: 'no-permission-block', title: t('dashboard.erpOverview'), children: [
              { type: 'minor', title: '%mnraLabel%', value: 'N/A', defaultValue: 'N/A' },
              { type: 'minor', title: '%engRequiredLabel%', value: 'N/A', defaultValue: 'N/A' },
              { type: 'minor', title: '%workOrderLabel%', value: 'N/A', defaultValue: 'N/A' }
            ]
          }
        ]
      }
    },
    {
      key: 'sensor-history',
      label: 'dashboard.sensorHistory',
      description: 'dashboard.sensorHistoryDesc',
      feature: 'advanced-metrics',
      singleton: true,
      includeInDefault: true,
      defaultLayout: {
        type: 'grid', feature: 'advanced-metrics', container: false, md: 12, xs: 12, children: [
          { type: 'component', componentName: 'DashboardHistoricalPanel' }
        ]
      }
    },
    {
      key: 'work-completed',
      label: 'dashboard.workCompleted',
      description: 'dashboard.workCompletedDesc',
      feature: 'work-tickets',
      singleton: true,
      includeInDefault: true,
      defaultLayout: {
        type: 'grid', feature: 'work-tickets', container: false, md: 6, xs: 12, children: [
          {
            id: 'block-work-completed', type: 'block', title: t('dashboard.workCompleted'), children: [
              { type: 'component', componentName: 'WorkCompletedGraph' }
            ]
          }
        ],
        fallback: [
          {
            id: 'block-work-completed', type: 'block', className: 'no-permission-block', title: t('dashboard.workCompleted'), children: [
              { type: 'image', image: workCompleted, style: { padding: '8px 0' } }
            ]
          }
        ]
      }
    },
    {
      key: 'alarms-by-status',
      label: 'dashboard.alarmsByStatus',
      description: 'dashboard.alarmsByStatusDesc',
      feature: null,
      singleton: true,
      includeInDefault: true,
      defaultLayout: {
        type: 'grid', container: false, md: 6, xs: 12, children: [
          {
            id: 'block-alarms-by-status', type: 'block', title: t('dashboard.alarmsByStatus'), children: [
              { type: 'component', componentName: 'AlarmsByStatusGraph' }
            ]
          }
        ]
      }
    },
    {
      key: 'new-work-tickets',
      label: 'dashboard.newWorkTickets',
      description: 'dashboard.newWorkTicketsDesc',
      feature: 'work-tickets',
      singleton: true,
      includeInDefault: true,
      defaultLayout: {
        type: 'grid', feature: 'work-tickets', container: false, md: 6, xs: 12, children: [
          {
            id: 'block-new-work-tickets', type: 'block', title: t('dashboard.newWorkTickets'), children: [
              { type: 'component', componentName: 'NewWorkTickets' }
            ]
          }
        ],
        fallback: [
          {
            id: 'block-new-work-tickets', type: 'block', className: 'no-permission-block', title: t('dashboard.newWorkTickets'), children: [
              { type: 'major', title: t('dashboard.newWorkTicketsToday'), value: 'N/A', defaultValue: 'N/A' },
              { type: 'major', title: t('dashboard.workTicketsClosed'), value: 'N/A', defaultValue: 'N/A' }
            ]
          }
        ]
      }
    },
    {
      key: 'critical-alarms',
      label: 'dashboard.criticalAlarms',
      description: 'dashboard.criticalAlarmsDesc',
      feature: 'criticality-ruleset',
      singleton: true,
      includeInDefault: true,
      defaultLayout: {
        type: 'grid', feature: 'criticality-ruleset', container: false, md: 6, xs: 12, children: [
          {
            type: 'block', title: t('dashboard.criticalAlarms'), children: [
              { type: 'component', componentName: 'CriticalAlarms' }
            ]
          }
        ],
        fallback: [
          {
            type: 'block', className: 'no-permission-block', title: t('dashboard.criticalAlarms'), children: [
              { type: 'major', title: t('dashboard.newCriticalAlarmsToday'), value: 'N/A', defaultValue: 'N/A' },
              { type: 'major', title: t('dashboard.alarmsComparedToYesterday'), value: 'N/A', defaultValue: 'N/A' }
            ]
          }
        ]
      }
    },
    {
      key: 'highest-critical-alarms',
      label: 'dashboard.highestCriticalAlarms',
      description: 'dashboard.highestCriticalAlarmsDesc',
      feature: null,
      singleton: true,
      includeInDefault: false,
      defaultLayout: {
        type: 'grid', container: false, md: 12, xs: 12, children: [
          {
            type: 'block', title: t('dashboard.highestCriticalAlarms'), children: [
              { type: 'component', componentName: 'HighestCriticalAlarmsList' }
            ]
          }
        ]
      }
    }
  ];
}
